import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SearchLine} from '@dropbox/dig-icons/assets';
import {snackbarAtom} from 'atoms/snackbar';
import type {WorkstreamBasic, WorkstreamNarrativeUrlAdminResponse} from 'client';
import {useSetAtom} from 'jotai';
import {useMemo, useState} from 'react';
import type {WorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';
import {WorkstreamTypeahead} from 'views/workstreams/WorkstreamTypeahead';

import styles from './AdminPillars.module.css';
import {useAdminNarrativeUrls, useUpdateAdminNarrativeUrl} from './useAdminNarrativeUrls';

const isValidUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const NarrativeUrlRow = ({
  row,
  isPending,
  onSave,
}: {
  row: WorkstreamNarrativeUrlAdminResponse;
  isPending: boolean;
  onSave: (narrativeUrl: string | null) => Promise<boolean>;
}) => {
  const saved = row.narrative_url ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? saved;
  const trimmed = value.trim();
  const isInvalid = Boolean(trimmed) && !isValidUrl(trimmed);
  const isDirty = trimmed !== saved;
  const name = row.workstream_name;

  return (
    <Box className={styles.narrativeLinkRow}>
      <Stack gap="4">
        <Text isBold>{name}</Text>
        {!row.is_pillar ? (
          <Text size="small" color="subtle">
            Workstream
          </Text>
        ) : null}
      </Stack>
      <Split gap="8" alignY="top">
        <Split.Item width="fill" className={styles.narrativeLinkInput}>
          <TextInput
            type="url"
            aria-label={`${name} narrative link`}
            placeholder="https://os.dropbox.com/planning/..."
            value={value}
            isInvalid={isInvalid}
            disabled={isPending}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          {isInvalid ? (
            <Text size="small" className={atoms({color: 'Alert Base'})}>
              Enter a valid http or https URL.
            </Text>
          ) : null}
        </Split.Item>
        <Button
          variant="primary"
          size="small"
          aria-label={`Save ${name} narrative link`}
          isLoading={isPending}
          disabled={!isDirty || isInvalid || isPending}
          onClick={() => {
            void onSave(trimmed || null).then((ok) => {
              if (ok) setDraft(null);
            });
          }}
        >
          {trimmed || !saved ? 'Save' : 'Clear'}
        </Button>
      </Split>
    </Box>
  );
};

/**
 * One narrative link per workstream: the period's pillars, then any other
 * workstream under its narrative that has one (or that's picked here).
 */
export const NarrativeUrlsPanel = ({
  timeframe,
  narrativeRootId,
  enabled,
}: {
  timeframe: WorkstreamTimeframe;
  narrativeRootId: number | undefined;
  enabled: boolean;
}) => {
  const setSnackbar = useSetAtom(snackbarAtom);
  const {data: savedRows = [], isLoading, isError} = useAdminNarrativeUrls(timeframe, {enabled});
  const updateNarrativeUrl = useUpdateAdminNarrativeUrl(timeframe);
  const [addedRows, setAddedRows] = useState<WorkstreamNarrativeUrlAdminResponse[]>([]);
  const rows = useMemo(() => {
    const savedIds = new Set(savedRows.map((row) => row.workstream_id));
    return [...savedRows, ...addedRows.filter((row) => !savedIds.has(row.workstream_id))];
  }, [addedRows, savedRows]);
  const excludedWorkstreamIds = useMemo(
    () => [...rows.map((row) => row.workstream_id), ...(narrativeRootId ? [narrativeRootId] : [])],
    [narrativeRootId, rows]
  );

  const handleSave = async (workstreamId: number, narrativeUrl: string | null) => {
    try {
      await updateNarrativeUrl.mutateAsync({workstreamId, narrativeUrl});
      setSnackbar({text: narrativeUrl ? 'Narrative link saved.' : 'Narrative link cleared.'});
      return true;
    } catch {
      setSnackbar({text: 'Could not save narrative link.'});
      return false;
    }
  };

  const pendingId = updateNarrativeUrl.isPending
    ? updateNarrativeUrl.variables?.workstreamId
    : undefined;

  return (
    <Box className={styles.narrativeLinksPanel}>
      <Stack gap="16">
        <Box className={styles.sectionHeader}>
          <Text isBold>Narrative links</Text>
          <Box className={styles.typeahead}>
            <WorkstreamTypeahead
              selectedIds={excludedWorkstreamIds}
              placeholder="Add a link to a workstream"
              inputIcon={<UIIcon src={SearchLine} className={styles.searchIcon} />}
              disabled={!narrativeRootId}
              disableCreateWorkstream
              excludeArchivedWorkstreams
              includeTrackers
              onSelect={(workstream: WorkstreamBasic) =>
                setAddedRows((current) => [
                  ...current,
                  {
                    workstream_id: workstream.id,
                    workstream_name: workstream.name,
                    is_pillar: false,
                    narrative_url: null,
                  },
                ])
              }
            />
          </Box>
        </Box>
        {isLoading ? (
          <Box display="flex" justifyContent="center">
            <Spinner size="small" />
          </Box>
        ) : isError ? (
          <Text color="subtle">Could not load narrative links. Refresh the page to try again.</Text>
        ) : rows.length ? (
          <Stack gap="12">
            {rows.map((row) => (
              <NarrativeUrlRow
                key={row.workstream_id}
                row={row}
                isPending={pendingId === row.workstream_id}
                onSave={(narrativeUrl) => handleSave(row.workstream_id, narrativeUrl)}
              />
            ))}
          </Stack>
        ) : (
          <Text color="subtle">No pillars are available for this planning period.</Text>
        )}
      </Stack>
    </Box>
  );
};
